import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  useBreakpoint,
  PaginationDisplayedItemCount
} from '@one-thd/sui-atomic-components';
import { ProductResultsPageLinks } from '@thd-olt-component-react/search-page-links';
import { updateQueryParams } from '../product-results-helpers';
import { ProductResultsContext } from './ProductResultsContext';
import { publish } from '../publisher';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';

import '../styles/results-pagination.scss';

const ResultsPagination = (props) => {
  const { data: productData } = useContext(ProductResultsContext);
  const matches = useBreakpoint('sm');

  const {
    onPageChange,
    pageSize,
    path,
    data: searchModelData,
  } = props;

  const data = getFirstNonEmptyObject(productData, searchModelData);

  const { products = [], searchReport } = data?.searchModel || {};
  if (!products.length || !searchReport) return null;

  const totalProducts = searchReport?.totalProducts > 696
    ? 696
    : searchReport?.totalProducts;

  const actualProductCount = searchReport?.totalProducts;
  const startIndex = searchReport?.startIndex || 0;

  const currentPage = Math.round(startIndex / pageSize) + 1;
  const totalPages = Math.ceil(totalProducts / pageSize);

  if (Number.isNaN(totalPages)
    || Number.isNaN(pageSize)
    || totalPages === 1
    || totalPages === Infinity) return null;

  const onChangeWrapper = (event, page) => {
    document.body.classList.add('results-pagination--fade');
    event.preventDefault();

    // The fade must paint before the page-change work runs. setTimeout(..., 0)
    // fired before the next paint and blocked it, so the screen looked frozen.
    // A nested rAF runs after a paint, so the fade shows first. INP 656ms -> 40ms.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (onPageChange) {
          window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('resetImpressionProvider');
          const url = updateQueryParams(path, [
            { paramName: 'Nao', paramValue: (page - 1) * pageSize }
          ]);
          onPageChange({ page, searchReport, url });
          publish('pagination.click');
        }
        document.body.classList.remove('results-pagination--fade');
      });
    });
  };

  return (
    <div className="sui-grow sui-pl-3" data-component="ResultsPagination">
      <div className="sui-relative
        sui-flex
        sui-items-center
        sui-gap-4
        sui-flex-col-reverse
        sm:sui-flex-row
        sm:sui-gap-0"
      >
        <PaginationDisplayedItemCount
          page={currentPage}
          itemsPerPage={pageSize}
          items={actualProductCount}
        />
        <ProductResultsPageLinks
          channel={matches.lesser ? 'mobile' : 'desktop'}
          path={path}
          pageSize={pageSize}
          paginationProps={{
            count: totalPages,
            page: currentPage,
            onChange: onChangeWrapper,
            fullWidth: matches.lesser,
          }}
        />
      </div>
    </div>
  );
};

ResultsPagination.defaultProps = {
  pageSize: 24,
  path: '',
  data: null
};

ResultsPagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  pageSize: PropTypes.number,
  path: PropTypes.string,
  data: PropTypes.shape({
    searchModel: PropTypes.shape({
      metadata: PropTypes.shape({
        contentType: PropTypes.string,
        h1Tag: PropTypes.string
      }),
      searchReport: PropTypes.shape({
        keyword: PropTypes.string,
        correctedKeyword: PropTypes.string,
        searchUrl: PropTypes.string
      }),
      taxonomy: PropTypes.shape({
        breadCrumbs: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.string,
          url: PropTypes.string,
          browseUrl: PropTypes.string,
          dimensionName: PropTypes.string,
          refinementKey: PropTypes.string
        }))
      })
    })
  })
};

ResultsPagination.displayName = 'ResultsPagination';

export { ResultsPagination };